import { Button, Dialog, Icon, Sheet, Text, TwTabs as Tabs } from '@ui-component-library';
import { TailwindWrapper } from '@utils/TailwindWrapper';
import { usePropsDataStore } from '../../store/PropsDataStore';
import { useNavigationStore } from "../../store/NavigationStore";

import { useState, useEffect, useRef } from 'react';
import { useAnimatedMount } from '@ui-component-library/hooks/useAnimatedMount';
import Cookies from 'js-cookie';
import { DataTab } from './DataTab';
import { EditTab } from './EditTab';
import { NotesTab } from './NotesTab';
import { ToolsTab } from './ToolsTab';
import { PlusTab } from './PlusTab';
import { updateSection, createAdFromSection as createAdFromSectionAPI, sectionTools } from '@services/sections';
import { useDemoMinskogStore } from '../../store/DemoMinskogStore';
import { DEMO_PROMPTS } from '../../constants/demoPrompts';

function findSectionByNr(activeProps, sectionID) {
  for (const prop of activeProps) {
    const sections = prop.data?.sections || [];
    const section = sections.find(sect => sect.nr == sectionID);
    if (section) return [section, prop];
  }
  return [null, null];
}

export default function SectionDetails() {
  const { props: properties, setOneProp } = usePropsDataStore()
  const { view, setView } = useNavigationStore()
  const { DemoBlockFunctionWrapper } = useDemoMinskogStore()
  const activeProps = properties.filter(p => p.active == true);

  const [loading, setLoading] = useState(false);
  const [loadingCreateAd, setLoadingCreateAd] = useState(false);
  const [editData, setEditData] = useState({});
  const [footerError, setFooterError] = useState(null);
  const [infoContent, setInfoContent] = useState(null);

  const [tab, setTab] = useState("data")

  const sectionID = view.section;
  const [foundSection, foundProp] = findSectionByNr(activeProps, sectionID);

  const stableDisplayRef = useRef<{ section: NonNullable<typeof foundSection>; prop: NonNullable<typeof foundProp> } | null>(null);
  if (foundSection && foundProp) {
    stableDisplayRef.current = { section: foundSection, prop: foundProp };
  }
  const resolved =
    foundSection && foundProp
      ? { section: foundSection, prop: foundProp }
      : stableDisplayRef.current;

  const isOpen = Boolean(view.section && activeProps?.length);
  const panelSurfaceKey =
    resolved?.section && resolved?.prop
      ? `${resolved.prop.ref}-${resolved.section.nr}`
      : null;
  const { shouldRender, animateIn, ref, onTransitionEnd } = useAnimatedMount(
    isOpen,
    350,
    panelSurfaceKey
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setView({ ...view, section: null });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setView, view]);

  useEffect(() => {
    setEditData({})
  }, [view.section])

  if (!shouldRender && !isOpen) {
    return null;
  }
  if (!resolved?.section || !resolved?.prop) {
    return null;
  }

  const section = resolved.section;
  const prop = resolved.prop;
  const panelSectionId = view.section ?? section.nr;
  const canEdit = prop?.role ? prop.role !== 'view' : !prop?.is_shared;

  const handleAndShowInfo = (name: string) => {
    const contentObj = name === 'data_information' ? {
      "title": "Information",
      "text": "Vi använder data från ett flertal källor, både öppna och licensierade. Några av de myndigheter och organisationer som bidrar med data är Lantmäteriet, Skogsstyrelsen, Naturvårdsverket, Riksantikvarieämbetet och Sveriges lantbruksuniversitet.\n\nVi vill rikta ett stort tack till samtliga data leverantörer för deras värdefulla arbete med att tillgängliggöra den information som möjliggör våra tjänster."
    } : null;
    setInfoContent(contentObj)
  }

  const setViewAndHistory = (comp: string, section: number) => {
    setView({comp: comp, section: section});
    history.pushState({comp: comp, section: section}, "");
  }

  const openSectionInMap = (sectionNumber, mode) => {
    if (view.comp != "karta") {
      const base = history.state && typeof history.state === "object" ? history.state : {};
      if (mode === "edit" || mode === "delete") {
        history.replaceState(
          { ...base, comp: "karta", section: sectionNumber },
          document.title,
          window.location.href
        );
      } else {
        history.pushState({ comp: "karta" }, "");
      }
    }
    setView({ ...view, comp: "karta", section: sectionNumber, leafletMode: mode });
  };

  const updateSectionData = (editData) => {

    if (editData.tall || editData.gran || editData.lov) {
      const tall = (editData?.tall ? editData.tall : section.tall) ?? 0;
      const gran = (editData?.gran ? editData.gran : section.gran) ?? 0;
      const lov = (editData?.lov ? editData.lov : section.lov) ?? 0;
      if ((Number(tall) + Number(gran) + Number(lov)) != 100) {
        setFooterError("Summan av TGL är inte 100%")
        return;
      }
    }

    DemoBlockFunctionWrapper(() => {
      setLoading(true)
      updateSection(editData, panelSectionId, prop.ref)
        .then(resp => {
          setLoading(false)
          if (resp.ok) {
            const propToUpdate = properties.find(p => p.ref === resp.data.ref);
            if (propToUpdate) {
              setOneProp({ ...propToUpdate, data: resp.data.new_data });
              if (view.comp == "karta") {
                openSectionInMap(panelSectionId, 'view')
              }
            }
          } else {
            setFooterError(resp.message || "Något gick fel")
          }
        })
        .catch(() => {
          setLoading(false)
          setFooterError("Något gick fel")
        })
    }, DEMO_PROMPTS.SAVE_CHANGES)
    
  }

  const handleNotesUpdated = (ref, newData) => {
    const propToUpdate = properties.find(p => p.ref === ref);
    if (propToUpdate) {
      setOneProp({ ...propToUpdate, data: newData });
    }
  }

  const createAdFromSection = () => {
    DemoBlockFunctionWrapper(() => {
      createAdFromSectionAPI(panelSectionId)
      .then(resp => {
        if (resp.ok) {
          Cookies.set(`ad_id_${(Window as any).user_data.loggedInUserID}`, resp.data.ad_id);
          setView({ comp: 'sell-timber', sellTimberAdId: resp.data.ad_id });
        } else {
          setFooterError(resp.message || "Något gick fel")
        }
      })
      .catch(() => {
        setFooterError("Något gick fel")
      })
      .finally(() => { setLoadingCreateAd(false); });
    }, DEMO_PROMPTS.CREATE_AD, () => {setLoadingCreateAd(false)})
    
  }

  const RunSectionTools = (toolName: string) => {
    DemoBlockFunctionWrapper(() => {
      if (toolName === 'edit' || toolName === 'delete') {
        openSectionInMap(panelSectionId, toolName);
        return;
      }
      setLoading(true)
      sectionTools(panelSectionId, toolName)
        .then(resp => {
          setLoading(false)
          if (resp.ok) {
            const propToUpdate = properties.find(p => p.ref === resp.data.ref);
            if (propToUpdate) {
              setOneProp({ ...propToUpdate, data: resp.data.new_data });
              openSectionInMap(panelSectionId, 'view')
            }
          } else {
            setFooterError(resp.message || "Något gick fel")
          }
        })
        .catch(() => {
          setLoading(false)
          setFooterError("Något gick fel")
        })
      }, DEMO_PROMPTS.RUN_TOOL)
  }

  const tabList = [
    {
      label: "Info",
      value: "data",
    },
    {
      label: "Anteckningar",
      value: "notes",
    },
    {
      label: "Ändra",
      value: "edit",
    },
    {
      label: "Verktyg",
      value: "tools",
    },
  ];

  const resetSectionView = () => {
    setView({ ...view, section: null });
  }

  return (
    <TailwindWrapper>
      <div ref={ref} className={`z-[1030] flex flex-col shrink-0 h-dvh overflow-hidden transition-width duration-300 ease-in-out md:ms-[16px] ${animateIn && isOpen ? 'w-full md:w-[360px]' : 'w-0'}`} onTransitionEnd={onTransitionEnd}>
        <div className="flex flex-col h-full border-l border-solid border-charcoal-alpha-50 bg-white rounded-l-xl">
          <Sheet.Header className="flex-wrap border-0">
            <div className="flex w-full">
              <Button variant="tertiary" size="icon" onClick={() => resetSectionView()}>
                <Icon name="ChevronDownSimple" className="rotate-90 fill-[#000000]" size="large" />
              </Button>
              <div className="flex rounded-lg-alt border border-solid border-[#E6E6E6] py-1-5 px-3 ms-2 items-center justify-center">
                <Icon name="MagnifyingGlassSimple" className="fill-[#000000] me-2" size="large" />
                <Text variant="text-sm/leading-normal/medium" className="capitalize">{prop.designation.toLowerCase()}</Text>
              </div>
              <div className="flex rounded-lg-alt border border-solid border-[#E6E6E6] py-1-5 px-3 ms-2">
                <Text variant="text-sm/leading-normal/medium" className="capitalize">{section.localID || section.nr}</Text>
              </div>
              <Button className="ml-auto" variant="tertiary" size="icon" onClick={() => resetSectionView()}>
                <Icon name="CrossSimple" className="fill-[#000000]" size="large" />
              </Button>
            </div>
          </Sheet.Header>
          <Sheet.Content className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
            <div className="flex w-full shrink-0 p-4">
              <Tabs
                value={tab}
                tabs={tabList}
                onChange={(value) => { setTab(value); setEditData({}); setFooterError(null) }}
              />
            </div>
            {tab === 'notes' && prop.dfr ? (
              <NotesTab
                section={section}
                prop={prop}
                onUpdated={handleNotesUpdated}
              />
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto">
            {tab == "data" && (
              <DataTab
                section={section}
                prop={prop}
                hasPlus={prop.dfr}
                handleAndShowInfo={handleAndShowInfo}
                createAdFromSection={createAdFromSection}
                loadingCreateAd={loadingCreateAd}
                setLoadingCreateAd={setLoadingCreateAd}
              />
            )}
            {tab == "edit" && (!canEdit ? (
              <div className="flex w-full p-4">
                <Text variant="text-sm/leading-normal/normal" className="text-[#737373]">
                  Du har endast läsbehörighet för den här fastigheten och kan inte redigera avdelningar.
                </Text>
              </div>
            ) : prop.dfr ? (
              <EditTab
                section={section}
                editData={editData}
                setEditData={setEditData}
                setFooterError={setFooterError}
              />
            ) : (
              <PlusTab text="Här kan du som pluskund enkelt redigera samtliga avdelningsdata" />
            ))}
            {tab == "tools" && (!canEdit ? (
              <div className="flex w-full p-4">
                <Text variant="text-sm/leading-normal/normal" className="text-[#737373]">
                  Du har endast läsbehörighet för den här fastigheten och kan inte använda verktygen.
                </Text>
              </div>
            ) : prop.dfr ? (
              <ToolsTab
                section={section}
                openSectionInMap={openSectionInMap}
                RunSectionTools={RunSectionTools}
              />
            ) : (
              <PlusTab text="Här kommer du som pluskund åt flera effektiva verktyg för att redigera dina avdelningar i kartan" />
            ))}
            {tab == "notes" && !prop.dfr && (
              <PlusTab text="Här kan du som pluskund snabbt och enkelt lägga till anteckningar per avdelning" />
            )}
              </div>
            )}
            <Dialog isOpen={infoContent} size="full">
              <Dialog.Header className="justify-between items-center">
                <Text variant="text-sm/leading-normal/medium">{infoContent?.title}</Text>
                <Button variant="outline" size="icon" onClick={() => setInfoContent(null)}>
                  <Icon name="CrossSimple" size="medium" />
                </Button>
              </Dialog.Header>
              <Dialog.Content>
                <Text variant="text-sm/leading-normal/normal" className="whitespace-pre-line">
                  {infoContent?.text}
                </Text>
              </Dialog.Content>
            </Dialog>
          </Sheet.Content>
          <Sheet.Footer className="flex gap-3 items-center justify-center">
            {footerError && <Text variant="text-sm/leading-normal/medium" className="py-2 px-4 text-danger-500">{footerError}</Text>}
            {(tab == "data" && view.comp !== "karta") && (
              <Button fullWidth variant="primary" className="bg-charcoal-900 text-white-50" onClick={() => setViewAndHistory("karta", section.nr)} disabled={loading}>
                {loading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-white-50 border-t-transparent" />
                ) : (
                  <Icon name="MapTrifold" size="large" className="fill-[#ffffff]"></Icon>
                )}
                Visa i kartan
              </Button>
            )}
            {(tab == "data" && view.comp === "karta") && (
              <Button fullWidth variant="primary" className="bg-charcoal-900 text-white-50" onClick={() => setViewAndHistory("overblick", section.nr)} disabled={loading}>
                {loading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-white-50 border-t-transparent" />
                ) : (
                  <Icon name="MapTrifold" size="large" className="fill-[#ffffff]"></Icon>
                )}
                Tillbaka till överblick
              </Button>
            )}
            {(tab == "edit" && prop.dfr && canEdit) && (
              <Button fullWidth variant="primary" disabled={!!footerError || loading} className="bg-charcoal-900 text-white-50" onClick={() => { updateSectionData(editData); }}>
                {loading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-white-50 border-t-transparent" />
                ) : (
                  <Icon name="CheckSimple" size="large" className="fill-[#ffffff]"></Icon>
                )}
                Spara ändringar
              </Button>
            )}
            <Button fullWidth variant="tertiary" onClick={() => resetSectionView()} disabled={loading}>
              Stäng
            </Button>
          </Sheet.Footer>
        </div>
      </div>
    </TailwindWrapper>
  );
}
